
// `SignStep` is the checkup's review-summary + Scope of Appointment step
// (task-17-brief.md, #598), and the renewal's too (the `aep` prop). Its product
// list is SoaProductCheckRow, the same checkbox row the scheduler's
// components/schedule/v2/steps/SoaStep.vue renders, so all three signing forms
// look and behave alike.
import _ from 'lodash'
import Callout from '../parts/Callout.vue'
import CheckCardGroup from '../parts/CheckCardGroup.vue'
import SoaProductCheckRow from '../parts/SoaProductCheckRow.vue'
import { address } from '~/util/string'

// Same coverage keys/labels as AboutStep.vue's COVERAGE_OPTIONS — duplicated
// here (rather than exported/imported) since it's a small static list; used
// by the About review card to list which other coverage the client checked.
// Display labels for `current_medicare_coverage`, phrased the way the renewal
// wizard's cards ask the question (components/checkup/steps/CurrentPlanStep.vue)
// so the review reads back what the client actually clicked.
const COVERAGE_TYPE_LABELS = {
    SUPP_PDP: 'Original Medicare + a Medigap and a drug plan',
    SUPP: 'Original Medicare + a Medigap',
    PDP: 'Original Medicare + a drug plan only',
    MAPD: 'A Medicare Advantage plan (all-in-one)',
    NONE: 'Original Medicare only',
    // No em dash and no call reference: this renders on /renewal/sign,
    // whose copy cleanup (#656 comment 2) bans both. Echoes step 2's own
    // "I'm not sure" answer copy.
    UNSURE: "Not sure yet. We'll figure it out together.",
}

// The checkup wizard's review rows. Kept as this component's default so
// /checkup/sign needs no change; /renewal/sign passes its own list.
const CHECKUP_CARDS = [
    { id: 'about', stepId: 'about' },
    { id: 'income', stepId: 'income' },
    { id: 'doctors', stepId: 'doctors' },
    // Doctors + hospitals share the 'doctors' step since #656 merged the two
    // pages; the review keeps separate rows, both jumping to the merged page.
    { id: 'hospitals', stepId: 'doctors' },
    { id: 'medications', stepId: 'medications' },
    { id: 'pharmacy', stepId: 'pharmacy' },
]

const COVERAGE_LABELS = {
    va: 'VA benefits',
    tricare: 'TRICARE / TRICARE For Life',
    champva: 'CHAMPVA',
    medicaid: 'Medicaid',
    fep_blue: 'FEP Blue / federal employee plan',
}

// Same values as IncomeStep.vue's quarterOptions — QUARTERS_40's wording
// matches the redesign demo's review copy (Survey.Redesign.Demo_7_31_6.html
// L2399); the other two reuse IncomeStep's own option labels verbatim, since
// the demo predates the real field's value set. Null/missing is omitted
// entirely (no lead-in sentence), not shown as "not sure".
const QUARTERS_LABELS = {
    QUARTERS_40: '10+ years Medicare-taxed work (premium-free Part A likely).',
    QUARTERS_30_39: "No, but I've paid for at least 7 years and 6 months (30 quarters).",
    QUARTERS_LT_30: "No, but I've paid for 7 years and 3 months or less (29 quarters or less).",
}

// Product ids/order are the scheduler's contract (components/schedule/
// StepByStepV2.vue's soaForBookPayload() persists these verbatim as
// SignedSoaForm.products / product_answers keys — see flow/consults/
// endpoints/survey.py's SignedSoaForm, which is the actual backend model
// for the /signed-soas/documenso/ + /replace/ endpoints this step posts to).
// Never rename an id without updating the backend + Salesforce mapping
// (same note as store/index.js's soaProducts getter, which carries the
// identical ids/icons for the v2 scheduler — kept as a separate, hardcoded
// list here rather than importing that getter so the checkup flow's copy
// can evolve independently). Icon names are verified against nuxt.config.js's
// FontAwesome whitelist (REGULAR_ICONS/SOLID_ICONS) — heart, prescription-
// bottle, shield-check and smile are all registered as-is, so no
// substitutions were needed here. Titles/descriptions/why-copy are verbatim
// from the redesign demo's SOA_PRODUCTS (temp-data/pre-consult-survey-
// redesign/Survey.Redesign.Demo_7_31_6.html, L2345-2354).
const SOA_PRODUCTS = [
    {
        id: 'mapd',
        icon: 'heart',
        title: 'Medicare Advantage (Part C)',
        description: 'One plan that bundles your hospital care, doctor visits, and usually your prescription drugs.',
        why: 'Medicare Advantage plans are offered by private insurance companies approved by Medicare. They combine Part A (hospital) and Part B (medical) in one plan, usually include drug coverage, and often add extras like dental and vision. Your advisor can compare them with Original Medicare on your call.',
    },
    {
        id: 'pdp',
        icon: 'prescription-bottle',
        title: 'Prescription Drug Plans (Part D)',
        description: 'Coverage that lowers what you pay for the medications you take.',
        why: 'Part D is a separate plan that helps pay for your prescriptions. Each plan covers drugs at different prices, so the right one depends on the exact medications you take. Your advisor can show which plans cover your list at the lowest cost.',
    },
    {
        id: 'supp',
        icon: 'shield-check',
        title: 'Medicare Supplement (Medigap)',
        description: 'Works alongside Original Medicare to help pay deductibles and copays.',
        why: 'Medigap plans work with Original Medicare to help pay costs like deductibles, copays, and coinsurance. They let you see any doctor who accepts Medicare, and are usually paired with a separate Part D drug plan.',
    },
    {
        id: 'dental_vision_hearing',
        icon: 'smile',
        title: 'Dental, Vision, and Hearing',
        description: "Routine dental, eye, and hearing care, which Medicare usually doesn't include.",
        why: 'Original Medicare usually does not cover routine dental cleanings, eye exams and glasses, or hearing aids. Standalone plans (or some Medicare Advantage plans) can add this coverage. Your advisor can walk you through the options.',
    },
]

// The AEP SOA overhaul (#656 comment 2) — renewal path ONLY, gated on the
// `aep` prop; /checkup/sign and the scheduler's SoaStep are untouched per
// Dien's bold warning. Copy is verbatim from the comment: no call or
// consultation references, no em dashes, no plan year anywhere on the form.
const AEP_COPY = {
    pageLede: "One last look at what you've shared, then a quick signature on the Scope of Appointment. It is a standard Medicare form that records which topics you'd like us to cover.",
    soaIntro: 'This form is required by Medicare. It records which topics you want us to cover, and it is what allows a licensed advisor to go over them with you. Please review it and add your signature below.',
    whyMedicare: 'The Centers for Medicare and Medicaid Services requires us to document the scope of appointment before a licensed advisor discusses specific plan types with you. Your selections stay valid for 12 months, so we can follow up on them without asking you again. A separate form is used for each Medicare beneficiary.',
    listHeading: "Tick the products you'd like us to include in your review:",
    attestation: 'By signing, you are telling us which products a licensed advisor may go over with you. That covers the products you ticked above. Signing does not affect your current or future enrollment in a Medicare plan, does not enroll you in a plan, and does not obligate you to enroll. Your selections stay valid for 12 months. All information on this form is confidential.',
}

// Every product starts ticked. Legal (Leah Luo, 2026-09-24): "Have every plan
// checked by default on the scope and they can uncheck if wanted." This used
// to pre-select only the products matching the client's current coverage
// (renewal) or nothing at all (checkup).
function allTicked () {
    return SOA_PRODUCTS.reduce((acc, p) => { acc[p.id] = true; return acc }, {})
}

// Exported (task-17-brief.md Step 1) so the spec can unit-test it directly.
// Mirrors the redesign demo's nameMatches (Survey.Redesign.Demo_7_31_6.html
// L2445-2450): tokenize both names, first + last token must match
// case-insensitively, a middle name/initial is forgiven, and both inputs
// need at least a first + last token.
export function nameMatches (input, expected) {
    const toks = s => String(s || '').toLowerCase().replace(/\./g, '').split(/\s+/).filter(Boolean)
    const a = toks(input)
    const b = toks(expected)
    if (a.length < 2 || b.length < 2) { return false }
    return a[0] === b[0] && a[a.length - 1] === b[b.length - 1]
}

export default {
    components: { Callout, CheckCardGroup, SoaProductCheckRow },
    props: {
        // The live survey (`$store.getters.survey`) — read-only here; review
        // cards and the beneficiary header are derived straight from it.
        survey: { type: Object, required: true },
        // `$store.getters.soa60days` — a signed-within-60-days Item ({id,
        // created, ...}), or null. Presence collapses the SOA block to a
        // read-only "already signed" notice; see the brief's decision point
        // on re-sign below (submit()).
        soa60days: { type: Object, default: null },
        // survey.skip_soa — hides the SOA block entirely (no collapsed
        // notice either) when an agent has waived it for this client.
        skipSoa: { type: Boolean, default: false },
        // Which review rows to show, and which step each row's Edit jumps to,
        // as `[{ id, stepId }]` (#656). The default is the checkup's own set,
        // so /checkup/sign renders exactly as before.
        //
        // This has to be a prop because `stepId` is wizard-specific: the
        // renewal wizard has no `about` or `income` step and calls doctors
        // `providers`, so hardcoded ids made three of the six Edit links
        // dead clicks there (goToStep no-ops on an unknown id).
        cards: { type: Array, default: () => CHECKUP_CARDS },
        // The AEP SOA mode (#656 comment 2): the comment's verbatim copy.
        // /renewal/sign passes it; /checkup/sign never does. Both modes use
        // the same pre-checked product checkboxes.
        aep: { type: Boolean, default: false },
    },
    data () {
        return {
            // The product checkboxes. Pre-checked here rather than from the
            // survey, so a survey that hydrates after mount (Dien's John Doe
            // QA, #656 comment 4 item 7) can neither race nor clobber them.
            ticked: allTicked(),
            signer: 'beneficiary',
            repName: '',
            repRelationship: '',
            typedName: '',
            submitting: false,
        }
    },
    computed: {
        products () {
            if (!this.aep) { return SOA_PRODUCTS }
            return SOA_PRODUCTS.map((p) => {
                const out = { ...p }
                // The one "What is this?" expansion that mentions a call.
                if (p.id === 'mapd') {
                    out.why = p.why.replace(' on your call.', ' in your review.')
                }
                // The Medigap pre-check explainer (mockup c2-11).
                if (p.id === 'supp' && this.survey.medigap_review_requested === true) {
                    out.note = "You told us earlier you'd like us to review your Medicare Supplement."
                }
                return out
            })
        },
        // AEP copy is verbatim from Dien's comment 2; the checkup strings are
        // the pre-#656 originals, byte for byte.
        pageLede () {
            return this.aep
                ? AEP_COPY.pageLede
                : "One last look at what you've shared, then a quick signature on the Scope of Appointment — a standard Medicare form that simply lists what we're allowed to discuss with you."
        },
        soaIntro () {
            return this.aep
                ? AEP_COPY.soaIntro
                : "This form is required by Medicare and allows you to select the topics we're allowed to discuss with you during your consultation. Please review the information below and complete the digital signature."
        },
        whyMedicare () {
            return this.aep
                ? AEP_COPY.whyMedicare
                : 'The Centers for Medicare and Medicaid Services requires us to document the scope of appointment prior to meeting with a Medicare beneficiary or their authorized representative. A separate form should be used for each Medicare beneficiary.'
        },
        listHeading () {
            return this.aep
                ? AEP_COPY.listHeading
                : "Tick the products you'd like to discuss on your call:"
        },
        attestation () {
            return this.aep
                ? AEP_COPY.attestation
                : 'By signing this form, you agree to meet with a licensed advisor to discuss the products you ticked above. Signing this form does not affect your current or future enrollment in a Medicare plan, enroll you in a Medicare plan, or obligate you to enroll in a Medicare plan. All information provided on this form is confidential.'
        },
        isRepresentative () { return this.signer === 'representative' },
        anyTicked () { return this.products.some(p => !!this.ticked[p.id]) },
        // What gates the signature, in both modes: at least one box ticked
        // (Dien: "Sign & submit disabled until at least one box is ticked").
        productsReady () { return this.anyTicked },
        repReady () {
            if (!this.isRepresentative) { return true }
            return !!((this.repName || '').trim() && (this.repRelationship || '').trim())
        },
        beneficiaryFullName () {
            return [this.survey.first_name, this.survey.last_name].filter(Boolean).join(' ').trim()
        },
        beneficiaryName () { return this.beneficiaryFullName || 'Your name' },
        beneficiaryMeta () {
            const s = this.survey
            const loc = [s.city, s.state].filter(Boolean).join(', ')
            const parts = []
            if (loc) { parts.push(s.zip_code ? `${loc} ${s.zip_code}` : loc) }
            if (s.cell_phone) { parts.push(s.cell_phone) }
            return parts.join(' · ')
        },
        initials () {
            const s = this.survey
            const a = (s.first_name || 'Y')[0] || 'Y'
            const b = (s.last_name || '')[0] || ''
            return (a + b).toUpperCase()
        },
        // Whose typed name has to match to sign: the representative's own
        // name when they're signing on the beneficiary's behalf, otherwise
        // the beneficiary's own name (#605 pattern, mirrored from SoaStep.vue).
        expectedSignerName () {
            return this.isRepresentative ? (this.repName || '').trim() : this.beneficiaryFullName
        },
        // Everything the signature needs BESIDES the signature itself — the
        // insert button stays disabled (with sigHelp explaining why) until
        // these are answered, mirroring SoaStep.vue's canSign.
        canInsert () {
            return this.productsReady && !!this.signer && this.repReady && !!this.expectedSignerName
        },
        nameReady () {
            // The name is inserted (never typed) now, so presence == match.
            return !!(this.typedName || '').trim()
        },
        canSubmit () {
            return this.productsReady && !!this.signer && this.repReady && this.nameReady
        },
        signerOptions () {
            return [
                { value: 'beneficiary', label: `I am the beneficiary, ${this.beneficiaryName}` },
                {
                    value: 'representative',
                    label: `I am an authorized representative, acting on behalf of ${this.beneficiaryName}`,
                    description: 'e.g. Power of Attorney (POA), Legal Guardian. You may be asked to provide documentation if a carrier requests it.',
                },
            ]
        },
        // Sign-button helper text state machine — mirrors DEMO's sigChange()
        // (Survey.Redesign.Demo_7_31_6.html L2451-2470), extended with the
        // representative-relationship requirement #605 added after the demo
        // was authored.
        sigHelp () {
            if (this.isRepresentative && !(this.repName || '').trim()) {
                return { text: 'Enter your name and relationship above first.', color: 'var(--ck-fg2)' }
            }
            if (!this.productsReady) {
                // Dien's verbatim validation text, now shared by both modes.
                return { text: 'Please tick at least one product above.', color: 'var(--ck-warning)' }
            }
            if (this.isRepresentative && !(this.repRelationship || '').trim()) {
                return { text: 'Enter your relationship to the beneficiary above.', color: 'var(--ck-fg2)' }
            }
            if (!(this.typedName || '').trim()) {
                return { text: 'Select Insert signature to activate Sign & submit.', color: 'var(--ck-fg2)' }
            }
            return { text: '✓ Ready. Press Sign & submit.', color: 'var(--ck-success)' }
        },
        // `$moment` is injected globally in the real app (plugins/extras.js)
        // — same convention WelcomeStep.vue's date fields already rely on;
        // specs mock it per-suite (see SignStep.spec.js), matching the
        // established pattern (test/checkup/WelcomeStep.spec.js).
        signedDateLabel () {
            const created = this.soa60days && this.soa60days.created
            if (!created) { return '' }
            const m = this.$moment(created)
            return m.isValid() ? m.format('MMMM D, YYYY') : ''
        },
        // The client's own SOA pdf. `signedSoaPdfUrl(null, key)` builds the
        // client-scoped route (/api/consults/survey/signed-soas/{key}/download/),
        // which resolves the SOA from the logged-in user — no uid, and no way
        // to ask for someone else's. Served inline, so it opens in the tab
        // rather than downloading.
        signedPdfUrl () {
            const soa = this.soa60days
            if (!soa || !soa.key || !(soa.value || {}).envelope_item_id) { return null }
            return this.$api.signedSoaPdfUrl(null, soa.key)
        },
        signedSentence () {
            const suffix = this.signedDateLabel ? ` on ${this.signedDateLabel}` : ''
            // No call references in AEP mode (#656 copy cleanup).
            return this.aep
                ? `You already signed a Scope of Appointment${suffix}. We have what we need for your review.`
                : `You already signed a Scope of Appointment${suffix}. Your advisor already has what they need for your call.`
        },
        // "Signature · {today}" next to the typed-name input in the active
        // (unsigned) state — DEMO L1486/L2510 sets this to today's date;
        // formatted the same way as signedDateLabel above.
        todaySignatureLabel () {
            return this.$moment().format('MMMM D, YYYY')
        },
        // One card per section (Interfaces, task-17-brief.md), read straight
        // off the live `survey` prop — mirrors DEMO's renderReview() (L2383-
        // 2434) using this survey's real field names instead of the demo's
        // `state.*` mirror. Each card's `body` is an array of lines (rendered
        // one `<div>` per line) rather than a single string, so the About/
        // Income cards can carry the demo's multi-line detail without
        // resorting to v-html.
        // Every row this component knows how to render, keyed by id. The
        // `cards` prop picks which ones appear, in what order, and where each
        // Edit link goes.
        cardCatalog () {
            const s = this.survey
            return {
                about: { icon: 'user', title: 'About you', ok: !!s.email, body: this.aboutLines },
                income: { icon: 'dollar-sign', title: 'Income', ok: true, body: this.incomeLines },
                currentPlan: {
                    icon: 'shield-alt',
                    title: 'Current coverage',
                    // 'UNSURE' is a complete answer, not a gap: the client told
                    // us they don't know, and their advisor picks it up on the
                    // call. Marking it incomplete would nag them about a
                    // question they already answered honestly.
                    ok: !!s.current_medicare_coverage,
                    body: this.currentPlanLines,
                },
                doctors: {
                    icon: 'stethoscope',
                    title: 'Doctors',
                    ok: !!((s.providers || []).length || s.no_providers),
                    body: (s.providers || []).length
                        ? [s.providers.map(p => (p.specialty ? `${p.name} (${p.specialty})` : p.name)).join(', ')]
                        : [s.no_providers ? 'No regular doctor to keep.' : 'None added'],
                },
                hospitals: {
                    icon: 'building',
                    title: 'Hospitals',
                    ok: !!((s.hospitals || []).length || s.no_hospitals),
                    body: (s.hospitals || []).length
                        ? [s.hospitals.map(h => h.name).join(', ')]
                        : [s.no_hospitals ? 'No preferred hospital.' : 'None added'],
                },
                medications: {
                    icon: 'prescription-bottle-alt',
                    title: 'Medications',
                    ok: !!((s.drugs || []).length || s.no_drugs),
                    body: (s.drugs || []).length
                        ? [s.drugs.map(d => this.drugText(d)).join(', ')]
                        : [s.no_drugs ? 'Not taking any medications.' : 'None added'],
                },
                pharmacy: {
                    icon: 'store',
                    title: 'Pharmacy',
                    ok: !!((s.pharmacies || []).length || s.no_pharmacies),
                    body: [this.pharmacyBody],
                },
                yourYear: {
                    icon: 'heartbeat',
                    title: 'Your year',
                    ok: !_.isNil(s.happy_medical) && !_.isNil(s.happy_prescription) && !_.isNil(s.health_changes),
                    body: this.yourYearLines,
                },
            }
        },
        reviewCards () {
            return this.cards
                .filter(c => this.cardCatalog[c.id])
                .map(c => ({ id: c.id, stepId: c.stepId, ...this.cardCatalog[c.id] }))
        },
        // Current coverage, for the renewal wizard's review. Reads the same
        // fields CurrentPlanStep writes.
        currentPlanLines () {
            const s = this.survey
            const coverage = s.current_medicare_coverage
            if (!coverage) { return ['Not answered yet'] }
            const lines = [COVERAGE_TYPE_LABELS[coverage] || coverage]
            // ' · ', not an em dash: this renders on /renewal/sign, whose
            // copy cleanup (#656 comment 2) bans em dashes page-wide. Plan
            // names ride along now that step 2 collects them (workstream 3).
            const named = [
                s.supp_carrier && `Medigap: ${[s.supp_carrier, s.supp_name].filter(Boolean).join(' · ')}`,
                s.pdp_carrier && `Part D: ${[s.pdp_carrier, s.pdp_name].filter(Boolean).join(' · ')}`,
                s.mapd_carrier && `Medicare Advantage: ${[s.mapd_carrier, s.mapd_name].filter(Boolean).join(' · ')}`,
            ].filter(Boolean)
            lines.push(...named)
            // Only surfaced on the unsure path, where it's the one identifying
            // detail the client could give us.
            if (coverage === 'UNSURE' && s.medicare_number) {
                lines.push(`Medicare number on file: ${s.medicare_number}`)
            }
            return lines
        },
        // Satisfaction + health changes, for the renewal wizard's review.
        yourYearLines () {
            const s = this.survey
            const say = v => (_.isNil(v) ? 'Not answered' : (v ? 'Happy' : 'Not happy'))
            const lines = [
                `Medical coverage: ${say(s.happy_medical)}`,
                `Prescription coverage: ${say(s.happy_prescription)}`,
            ]
            if (!_.isNil(s.health_changes)) {
                lines.push(s.health_changes ? 'Health changed this year' : 'No major health changes')
            }
            return lines
        },
        // DOB, address, Medicare/HSA/Social-Security status, other coverage,
        // and chronic conditions — mirrors DEMO's renderReview() About body
        // (L2389-2395), using this survey's real field names (and the same
        // COVERAGE_LABELS/possibleCsnpConditions sources AboutStep.vue reads
        // from) instead of the demo's `state.*`/DOM-scraped mirror.
        aboutLines () {
            const s = this.survey
            const lines = []
            const nameDob = [this.beneficiaryFullName || null, s.dob ? `DOB ${this.$moment(s.dob).format('MM/DD/YYYY')}` : null]
                .filter(Boolean).join(' · ')
            if (nameDob) { lines.push(nameDob) }
            lines.push(s.email || 'Email missing')
            if (s.cell_phone) { lines.push(s.cell_phone) }
            const addr = address(s).trim()
            if (addr) { lines.push(addr) }
            // 'Not on file', not the '—' placeholder: it reads as an answer
            // rather than a rendering glitch, and the renewal sign page bans
            // em dashes outright (#656 copy cleanup).
            const medicareLabel = this.optionText(this.$store.getters.medicareParts, s.medicare_parts, 'Not on file')
            lines.push(`Medicare: ${medicareLabel} · HSA: ${s.hsa ? 'Yes' : 'No'} · Social Security: ${s.collecting_ssi ? 'Yes' : 'No'}`)
            const coverage = Object.keys(COVERAGE_LABELS).filter(k => s[k]).map(k => COVERAGE_LABELS[k])
            if (coverage.length) { lines.push(`Other coverage: ${coverage.join(', ')}`) }
            const conditions = (this.$store.getters.possibleCsnpConditions || []).filter(c => s[c.slug]).map(c => c.name)
            if (conditions.length) { lines.push(`Conditions: ${conditions.join(', ')}`) }
            return lines
        },
        incomeLines () {
            const s = this.survey
            if (!s.tax_filing_status && !s.current_income) {
                return ["Skipped. We'll show standard Medicare pricing."]
            }
            const parts = []
            if (QUARTERS_LABELS[s.tax_quarters_paid]) { parts.push(QUARTERS_LABELS[s.tax_quarters_paid]) }
            const filingLabels = { SINGLE: 'Single', MARRIED_JOINT: 'Married filing jointly', MARRIED_SEPARATELY: 'Married filing separately' }
            if (s.tax_filing_status) { parts.push(`Filed as ${filingLabels[s.tax_filing_status] || s.tax_filing_status}.`) }
            if (s.current_income) { parts.push('Income range on file.') }
            return [parts.join(' ')]
        },
        pharmacyBody () {
            const s = this.survey
            let base
            if ((s.pharmacies || []).length) {
                base = s.pharmacies.map(p => p.name).join(', ')
            } else {
                base = s.no_pharmacies ? 'No preferred pharmacy.' : 'None selected'
            }
            const suffixes = []
            if (s.prefer_mail_order) { suffixes.push('Prefers mail order') }
            if (s.other_pharmacies_ok) { suffixes.push('Open to other pharmacies') }
            return suffixes.length ? `${base} · ${suffixes.join(' · ')}` : base
        },
    },
    watch: {
        // A signer switch or a representative-name edit invalidates an
        // already inserted signature — it may carry the wrong name.
        signer () { this.typedName = '' },
        repName () { if (this.typedName) { this.typedName = '' } },
    },
    methods: {
        // One click inserts the signer's name (Aziz QA 2026-09-01 — parity
        // with SoaStep.vue's insertSignature; the payload's typed_name is
        // unchanged, it just can't be mistyped anymore).
        insertSignature () {
            if (!this.canInsert) { return }
            this.typedName = this.expectedSignerName
        },
        // Same fallback convention as WelcomeStep.vue's optionText(), with the
        // fallback overridable (see aboutLines' Medicare segment).
        optionText (options, value, fallback = '—') { return ((options || []).find(o => o.value === value) || {}).text || fallback },
        // Dosage/custom-dosage text for a stored Drug — same shape/precedent
        // as MedicationsStep.vue's drugSub().
        drugText (d) {
            const parts = []
            if (d.dosage && d.dosage.strength) {
                parts.push(`${d.dosage.strength}${(d.dosage.strength_units || '').toLowerCase()} ${(d.dosage.form || '').toLowerCase()}`.trim())
            }
            if (d.custom_dosage) { parts.push(d.custom_dosage) }
            return parts.length ? `${d.name} (${parts.join(' · ')})` : d.name
        },
        setTick (id, v) { this.$set(this.ticked, id, v) },
        setSigner (v) { this.signer = v },
        // Payload contract copied from components/schedule/StepByStepV2.vue's
        // soaForBookPayload() (~L1098-1117) and confirmed against the actual
        // backend model, flow/consults/endpoints/survey.py's SignedSoaForm —
        // the two agree exactly (full_name, address, phone, role, typed_name,
        // products, product_answers, representative_name/relationship).
        //
        // `products` (booleans) is the authorization: the Documenso PDF-filling
        // that renders the legal document (flow/services/documenso.py's
        // _product_selected()/build_soa_field_values()) reads it alone to decide
        // which boxes are checked, and never looks at `product_answers`.
        //
        // Re-sign decision (brief's decision point): SoaStep.vue — the
        // component this step's SOA UI is adapted from — never calls
        // replaceSignedSoa; its unsign()/insertSignature() only flip local
        // wizard state, and the actual signed-SOA API call happens later, at
        // booking time, as a plain create (soaForBookPayload() has no replace
        // branch either). Only the separate, legacy components/form/ESoa.vue
        // has a "sign a new SOA" affordance that calls replaceSignedSoa, and
        // that's not the component the brief points at. Since this step also
        // only ever shows a read-only "already signed" notice once soa60days
        // exists (no re-sign affordance, per the brief), createSignedSoaDocumenso
        // is the only call this step ever needs to make.
        async submit () {
            if (!this.canSubmit) { return }
            const s = this.survey
            const repFields = this.signer === 'representative'
                ? {
                    representative_name: this.repName.trim(),
                    representative_relationship: this.repRelationship.trim(),
                }
                : {}
            // The checkbox map is the product authorization (checked reads
            // back as 'yes', unchecked as 'no'), plus products_presented, the
            // list actually rendered (Dien's build note, #656: store which
            // products were PRESENTED, not just selected).
            const productFields = {
                products: { ...this.ticked },
                product_answers: _.mapValues(this.ticked, v => (v ? 'yes' : 'no')),
                products_presented: this.products.map(p => p.id),
            }
            // Deferred countersigning (2026-09-30): a renewal SOA is stored as
            // the client signed it and countersigned when the AEP
            // recommendation is sent, by whoever sends it -- the send is the
            // appointment (Leah Luo). The backend keys that on `aep`; the
            // checkup omits it and is countersigned right away by the owner.
            const aepFields = this.aep ? { aep: true } : {}
            const payload = {
                full_name: [s.first_name, s.last_name].filter(Boolean).join(' '),
                address: [s.street_1, s.city, s.state, s.zip_code].filter(Boolean).join(', ') || null,
                phone: s.cell_phone || null,
                role: this.signer,
                typed_name: this.typedName.trim(),
                ...productFields,
                ...repFields,
                ...aepFields,
            }
            this.submitting = true
            const resp = await this.$try('createSignedSoaDocumenso', payload)
            this.submitting = false
            if (resp && !resp.error) { this.$emit('signed', resp) }
        },
    },
}
